<script setup lang="ts">
  import { computed } from 'vue'
  import { Phone } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'

  interface Props {
    sourceCallUuid?: string | null
    caseType?: string | null
    callTime?: string | null
    variant?: 'default' | 'subtle'
  }

  const props = withDefaults(defineProps<Props>(), {
    sourceCallUuid: null,
    caseType: null,
    callTime: null,
    variant: 'default'
  })

  const { t, locale } = useTranslation({
    en: {
      viewCall: 'View call',
      viewCallAt: 'View call from {dateTime}'
    },
    de: {
      viewCall: 'Zum Anruf',
      viewCallAt: 'Zum Anruf vom {dateTime}'
    },
    fr: {
      viewCall: "Voir l'appel",
      viewCallAt: "Voir l'appel du {dateTime}"
    }
  })

  const localeMap: Record<string, string> = {
    en: 'en-GB',
    de: 'de-DE',
    fr: 'fr-FR'
  }

  const isVisible = computed(() => Boolean(props.sourceCallUuid) && props.caseType === 'PHONE')

  const callUrl = computed(() =>
    ROUTES.APP.CALLS.CALL_DETAIL.replace(':uuid', props.sourceCallUuid!)
  )

  const formattedCallTime = computed(() => {
    if (!props.callTime) return ''
    const dateLocale = localeMap[locale.value] || 'de-DE'
    return new Date(props.callTime).toLocaleString(dateLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  })

  const linkLabel = computed(() => {
    if (formattedCallTime.value) {
      return t('viewCallAt', { dateTime: formattedCallTime.value })
    }
    return t('viewCall')
  })

  const linkClass = computed(() => {
    if (props.variant === 'subtle') {
      return 'inline-flex items-center gap-1 text-sm text-stone-500 hover:text-purple-600'
    }
    return 'inline-flex items-center gap-1.5 text-sm font-medium text-purple-600 hover:text-purple-700'
  })
</script>

<template>
  <a
    v-if="isVisible"
    :href="callUrl"
    :class="linkClass"
    data-cy="source-call-link"
  >
    <Phone
      class="size-4 shrink-0"
      aria-hidden="true"
    />
    {{ linkLabel }}
  </a>
</template>
